import { ulid } from '@w/utils'
import { prepareForLog } from './prepare-for-log/prepare-for-log.js'
import { prepareTelError } from './prepare-for-log/prepare-tel-error.js'
import type { TelErrorData } from './tel-error-data.js'
import { type SpanData, type Span, getBaggage, type ResourceUserInfo } from './tel-types.js'
import { safe } from '@w/utils'
import { getRoughObjectSize } from '@w/utils'

/**
 * What a span records as its output on the DEFAULT path — output capture is
 * fail-closed, so this is what almost every span in the fleet records.
 *
 * A db span keeps its VOLUME — the row count, read off an array of rows or off a
 * driver result that counts them itself — so a query that got slow because it
 * returned forty thousand rows is still diagnosable without one of those rows
 * reaching the log pipeline. Anything else records nothing: a generic span's
 * output is whatever its callback happened to return, and there is no shape to
 * that which is safe by construction.
 */
function outputVolume(data: SpanData, output: unknown): { rowCount: number } | undefined {
  if (data.target !== 'db') return undefined
  if (Array.isArray(output)) return { rowCount: output.length }
  if (typeof output === 'object' && output !== null && 'rowCount' in output) {
    const { rowCount } = output as { rowCount: unknown }
    if (typeof rowCount === 'number') return { rowCount }
  }
  return undefined
}

/**
 * The single gate every output goes through — `end()`, `update()`, db span or
 * not. INPUT is prepared elsewhere, but its SIZE keeps the same contract as
 * `outputSize` below: measured on the raw value.
 *
 * `outputSize` describes the REAL payload, measured on the raw value before
 * anything is truncated or discarded — on the capturing branch too, where the
 * recorded value is the prepared one. @w/be-otel-export exports it as the
 * span's `output.size`, so a dashboard that watches a response growing keeps
 * its signal on the default path, where the payload itself is never recorded —
 * a db span that suddenly returns forty megabytes still says so. Sizing walks
 * the value rather than serialising it, so withholding a payload costs no copy
 * of it.
 *
 * A Response records nothing in either mode: its body is a one-shot stream, so
 * sizing would walk the wrapper and consuming it is not an option. The check
 * runs HERE, on the raw value — prepareForLog returns a Response untouched and
 * outputVolume never produces one, so a caller inspecting the gated output
 * instead would only ever see a Response on the capturing branch.
 */
function gateOutput(
  data: SpanData,
  output: unknown,
  captures: boolean,
): { output: unknown; outputSize: number | undefined; isResponse: boolean } {
  if (output instanceof Response) {
    return { output: undefined, outputSize: undefined, isResponse: true }
  }
  const outputSize = output === undefined ? undefined : getRoughObjectSize(output)
  if (captures) {
    return { output: prepareForLog(output, { removeMark: true }), outputSize, isResponse: false }
  }
  return { output: outputVolume(data, output), outputSize, isResponse: false }
}

export function _makeSpan(
  data: SpanData,
  storage: {
    end: (patch: Partial<SpanData> & { id: string }) => void
    update: (patch: Partial<SpanData> & { id: string }) => void
    errors: {
      create: (originalError: unknown, data: TelErrorData, userInfo?: ResourceUserInfo) => void
    }
    getErrorFilterStrings: () => string[]
    getErrorMapper?: () => ((error: unknown) => unknown) | undefined
    /**
     * REQUIRED, unlike everything above it. A default here would be a second
     * declaration of the fail-closed policy, and the one direction it could
     * drift in is open — see `TelStorageConfig.captureSpanOutput`, which is
     * where the default lives.
     */
    isSpanOutputCaptured: () => boolean
  },
  // Named spanOpts, not opts: end(patch, opts) takes a per-CALL opts parameter,
  // and a plain `opts` here would be shadowed inside end() — a future line there
  // meaning the span-level override would silently read the per-call one.
  spanOpts: { captureOutput?: boolean } = {},
): Span {
  const createdAt = Date.now()
  data.id = data.id || ulid()
  // Sized before prepareForLog: the prepared value is truncated for logging,
  // and inputSize means the real payload — the contract outputSize keeps too.
  if (data.input !== undefined) {
    data.inputSize = getRoughObjectSize(data.input)
  }
  data.input = prepareForLog(data.input, { removeMark: true })

  return {
    data, // TODO: make sure that it is not detached from storage (same reference)

    // A method rather than a closure built per span: `end` and `update` are on
    // the object anyway, so this costs nothing, and it is the same answer the
    // producers get. Resolved per CALL - the composition root configures the
    // storage before any span ends, and a test may flip it between spans.
    capturesOutput() {
      return spanOpts.captureOutput ?? storage.isSpanOutputCaptured()
    },

    update(patch) {
      let properPatch = { ...patch }
      // Key PRESENCE, like the output gate below: `{ input: 0 }` and
      // `{ input: '' }` are inputs a caller really recorded, and a truthy test
      // skipped their sizing (primitives pass prepareForLog unchanged, so
      // masking never depended on this - the size did).
      if ('input' in patch) {
        properPatch.inputSize =
          patch.input === undefined ? undefined : getRoughObjectSize(patch.input)
        properPatch.input = prepareForLog(patch.input, { removeMark: true })
      }
      // Key PRESENCE, not truthiness. `{ output: 0 }`, `{ output: '' }` and
      // `{ output: false }` are outputs a handler really produced, and a truthy
      // test walked past them - past the capture gate AND past prepareForLog,
      // so they reached the span unmasked whatever the service had configured.
      if ('output' in patch) {
        const gated = gateOutput(this.data, patch.output, this.capturesOutput())
        if (gated.isResponse) {
          // Recorded nowhere, and not left in the patch either: properPatch
          // started as a spread of patch, so the Response is still in there.
          // The keys LEAVE the patch rather than turning undefined, so a patch
          // that also carries other fields cannot erase what an earlier
          // update() recorded - the same keep-prior rule end() applies.
          const { output: _output, outputSize: _outputSize, ...rest } = properPatch
          properPatch = rest
        } else {
          properPatch.output = gated.output
          properPatch.outputSize = gated.outputSize
        }
      }
      // Nothing survived, and nothing else was asked for. Under a fail-closed
      // default this is the common shape of an output-only patch, and writing
      // it would cost a linear scan of the span store plus an exporter
      // onUpdate event for a patch that changes nothing. Only the gated fields
      // can empty out, so anything else the caller sent still counts.
      if (
        properPatch.output === undefined &&
        properPatch.outputSize === undefined &&
        Object.keys(properPatch).every((key) => key === 'output' || key === 'outputSize')
      ) {
        return
      }
      this.data = {
        ...this.data,
        ...properPatch,
      }
      storage.update(this.data)
    },
    end(patch, opts) {
      // `!== undefined`, not truthiness: a span that finished inside the same
      // millisecond has duration 0, and a falsy guard let exactly those end
      // twice - overwriting the output and re-reporting the error of the
      // fastest spans in the process. eg we can end span with error in
      // startSpan (instead of throwing it) and internally it shouldn't end it
      // again.
      if (this.data.duration !== undefined) return

      // Apply error mapper if configured
      const errorMapper = storage.getErrorMapper ? storage.getErrorMapper() : undefined
      const mappedError = patch.error && errorMapper ? errorMapper(patch.error) : patch.error

      const patchError = mappedError
        ? prepareTelError({ error: mappedError, span: this.data })
        : undefined
      const gated = gateOutput(this.data, patch.output, this.capturesOutput())

      if (!gated.isResponse) {
        this.data.output = gated.output
        if (gated.outputSize !== undefined) {
          this.data.outputSize = gated.outputSize
        }
      }

      this.data.duration = Date.now() - createdAt

      if (patchError) {
        const error = patchError

        // downgrade error level
        if (error.origin === 'server') {
          if (error.status < 500) error.level = 'warning'
        } else {
          if (
            error.serverStatus === 401 ||
            error.serverStatus === 403 ||
            error.serverStatus === 404
          )
            error.level = 'warning'

          if (error.serverStatus) {
            error.duplicated = true
          }
        }

        // Check if it's a domain error and downgrade to warning
        if (error.type === 'domain') {
          error.level = 'warning'
        }

        // Check if error message contains any filter strings and downgrade to warning
        if (storage.getErrorFilterStrings) {
          const errorFilterStrings = storage.getErrorFilterStrings()
          if (errorFilterStrings.length > 0 && error.message) {
            for (const filterString of errorFilterStrings) {
              if (error.message.includes(filterString)) {
                error.level = 'warning'
                break
              }
            }
          }
        }

        // Manual error level override has highest priority
        if (opts?.errorLevel) {
          error.level = opts.errorLevel
        }

        this.data.error = error
      }

      const status =
        this.data.error?.level === 'warning' ? 'warning' : this.data.error ? 'error' : 'ok'

      const storagePatch: Partial<SpanData> & { id: string } = {
        error: this.data.error,
        output: this.data.output,
        outputSize: this.data.outputSize,
        duration: this.data.duration,
        id: this.data.id,
        status,
        attributes: patch.attributes,
      }
      if (patch.name) storagePatch.name = patch.name
      storage.end(storagePatch)

      if (status === 'error') {
        storage.errors.create(mappedError, safe(this.data.error))
      }
    },
    _getBaggage() {
      return getBaggage(this.data)
    },
  }
}
